import { ArrowRight, Check, Loader2, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  useAdminPayoutProviders,
  useAdminTeams,
  useCheckProvidersNow,
  type HealthSnapshot,
  type PayoutProviderInfo,
  type RequiredInviterField,
} from '@/hooks/useAdmin';
import { providerLabel } from '@/lib/labels';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tag, channelLabel } from '@/components/ui/tag';
import { PageHeader } from '@/components/ui/page-header';
import { INVITE_CHANNELS } from '@/api/types';

/** Outreacher details a provider needs before it can pay them. */
const FIELD_LABELS: Record<RequiredInviterField, string> = {
  clippingId: 'Clipping account',
  paypalEmail: 'PayPal email',
  email: 'Email',
  inviterPublicId: 'Outreacher ID',
  name: 'Name',
};

/** What each verification source is trusted for. */
const ASPECT_LABELS: Record<string, string> = {
  IDENTITY: 'Who the creator is',
  CLIP_ACTIVITY: 'Joins, posts and views',
  SOCIAL_FOLLOWERS: 'Follower counts',
};

/**
 * Read-only view of the routing config + provider capability matrix.
 *
 * Nothing sensitive is exposed — no API keys, no base URLs, no secret-key
 * fragments. Just the abstract routing rules (which team goes to which
 * provider) and what each provider supports (callbacks vs poll-status vs
 * cancel). Useful for confirming "where will Alice's payout actually land".
 */
export function AdminProviders(): JSX.Element {
  const query = useAdminPayoutProviders();
  const teams = useAdminTeams();
  const checkNow = useCheckProvidersNow();

  function runCheck(): void {
    checkNow.mutate(undefined, {
      onSuccess: (data) => {
        const okCount =
          data.providers.filter((p) => p.health?.ok).length +
          data.verificationProviders.filter((v) => v.health?.ok).length;
        const total = data.providers.length + data.verificationProviders.length;
        toast.success(`Checked ${total} connection${total === 1 ? '' : 's'}: ${okCount} connected`);
      },
      onError: (err: Error) => toast.error(`Couldn't run the check: ${err.message}`),
    });
  }

  if (query.isLoading) {
    return (
      <div className="flex items-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <p className="text-sm text-destructive">
        Couldn&apos;t load payout providers{query.error ? `: ${(query.error as Error).message}` : '.'}
      </p>
    );
  }

  const { providers, verificationProviders, routing } = query.data;
  const providerByKey = new Map<string, PayoutProviderInfo>(providers.map((p) => [p.key, p]));
  const builtIn = routing.byTeam as Record<string, string>;
  // The provider each team actually uses: the team's own default if set,
  // otherwise the built-in default (per-outreacher overrides aside).
  const teamRows = (teams.data ?? []).map((t) => {
    const key = t.defaultPayoutProviderKey ?? builtIn[t.code] ?? routing.default;
    return {
      code: t.code,
      name: t.displayName,
      isActive: t.isActive,
      key,
      source: t.defaultPayoutProviderKey ? 'Team default' : 'Built-in default',
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payout providers"
        description="Who pays each outreacher, and whether each connection is working. The provider is fixed when a payout is created, so changing it doesn't move payouts already sent."
      />

      <Card>
        <CardHeader>
          <CardTitle>Where each team is paid</CardTitle>
          <p className="max-w-[70ch] text-sm text-muted-foreground">
            An outreacher is paid through the first of these that is set: their own override, then their
            team&apos;s default, then the built-in default. Team defaults are set on Setup → Teams and apply
            straight away. Per-outreacher overrides have no screen yet; engineering sets them. Only the
            built-in defaults need a code release to change.
          </p>
        </CardHeader>
        <CardContent>
          <table className="responsive-stack w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="pb-2">Team</th>
                <th className="hidden pb-2 sm:table-cell">
                  <span className="sr-only">Paid through</span>
                </th>
                <th className="pb-2">Provider</th>
                <th className="pb-2">Set by</th>
                <th className="pb-2">How payments are confirmed</th>
              </tr>
            </thead>
            <tbody>
              {teamRows.map((t) => (
                <tr key={t.code} className="border-t transition-colors hover:bg-accent/40">
                  <td className="py-2 font-medium">
                    {t.name}
                    {!t.isActive && <span className="ml-1 text-xs font-normal text-muted-foreground">(off)</span>}
                  </td>
                  <td className="hidden py-2 text-muted-foreground sm:table-cell">
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </td>
                  <td className="py-2" data-label="Provider">
                    {providerLabel(t.key)}
                  </td>
                  <td className="py-2 text-xs text-muted-foreground" data-label="Set by">
                    {t.source}
                  </td>
                  <td className="py-2 text-xs text-muted-foreground" data-label="Confirmed">
                    {settlementHint(providerByKey.get(t.key))}
                  </td>
                </tr>
              ))}
              <tr className="border-t transition-colors hover:bg-accent/40">
                <td className="py-2 italic text-muted-foreground">Any other team</td>
                <td className="hidden py-2 text-muted-foreground sm:table-cell">
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </td>
                <td className="py-2" data-label="Provider">
                  {providerLabel(routing.default)}
                </td>
                <td className="py-2 text-xs text-muted-foreground" data-label="Set by">
                  Built-in default
                </td>
                <td className="py-2 text-xs text-muted-foreground" data-label="Confirmed">
                  {settlementHint(providerByKey.get(routing.default))}
                </td>
              </tr>
            </tbody>
          </table>
          {teams.isLoading && <p className="mt-2 text-xs text-muted-foreground">Loading teams…</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle>Payout providers</CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={runCheck}
            disabled={checkNow.isPending}
            title="Check every connection now instead of waiting for the next automatic check (every minute)"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${checkNow.isPending ? 'animate-spin' : ''}`} />
            {checkNow.isPending ? 'Checking…' : 'Check now'}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {providers.map((p) => (
              <div
                key={p.key}
                className="grid gap-3 rounded-md bg-muted/30 p-4 sm:grid-cols-[220px_1fr]"
              >
                <div className="flex flex-col gap-1.5">
                  <span className="text-base font-semibold leading-tight">{providerLabel(p.key)}</span>
                  <span className="text-xs text-muted-foreground">{settlementHint(p)}</span>
                  <HealthBlock health={p.health} />
                </div>
                <div className="flex flex-col gap-3">
                  {p.attributionNote && (
                    <p className="text-sm text-foreground/85">{p.attributionNote}</p>
                  )}
                  <div className="flex flex-col gap-1.5">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Outreacher details needed
                    </span>
                    {p.requiredInviterFields.length === 0 ? (
                      <span className="text-xs text-muted-foreground">None</span>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {p.requiredInviterFields.map((f) => (
                          <Tag key={f}>{FIELD_LABELS[f] ?? 'Other detail'}</Tag>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs">
                    <CapLine label="Reports back when paid" value={p.capabilities.callbacks} />
                    <CapLine label="Status can be checked" value={p.capabilities.syncStatus} />
                    <CapLine label="Payouts can be cancelled" value={p.capabilities.cancel} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            These details are checked before a payout is created. If an outreacher is missing one,
            Pay is turned off for them on Payroll and the row says what to add.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Verification sources</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {verificationProviders.map((v) => (
              <div
                key={v.key}
                className="grid gap-3 rounded-md bg-muted/30 p-4 sm:grid-cols-[220px_1fr]"
              >
                <div className="flex flex-col gap-1.5">
                  <span className="text-base font-semibold leading-tight">{v.displayName}</span>
                  <span className="text-xs text-muted-foreground">
                    {v.channels.length >= INVITE_CHANNELS.length
                      ? 'All channels'
                      : v.channels.map((c) => channelLabel(c)).join(' · ')}
                  </span>
                  <HealthBlock health={v.health} />
                </div>
                <div className="flex flex-col gap-3">
                  {v.sourceNote && (
                    <p className="text-sm text-foreground/85">{v.sourceNote}</p>
                  )}
                  <div className="flex flex-col gap-1.5">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Used for
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {v.capabilities.map((c) => (
                        <Tag key={c}>{ASPECT_LABELS[c] ?? 'Other checks'}</Tag>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Each check goes to the source listed for it: who the creator is and their joins, posts and
            views come from Clipping; follower counts come from the social lookup service. Every
            connection is checked once a minute, so reloading this page doesn&apos;t contact them
            again.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function HealthBlock({ health }: { health: HealthSnapshot | null }): JSX.Element {
  if (!health) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
        Not checked yet
      </span>
    );
  }
  const tone = health.ok ? 'success' : 'destructive';
  const dot = health.ok ? 'bg-success' : 'bg-destructive';
  return (
    <div className="flex flex-col gap-1">
      <span className="inline-flex items-center gap-1.5 text-xs">
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${dot}`} />
        <span className={tone === 'success' ? 'text-foreground' : 'text-destructive'}>
          {health.ok ? 'Connected' : 'Unreachable'}
        </span>
        {typeof health.latencyMs === 'number' && (
          <span className="font-mono text-[10px] text-muted-foreground">
            {health.latencyMs}ms
          </span>
        )}
      </span>
      <span className="text-[10px] text-muted-foreground">
        Last checked {relativeTime(health.checkedAt)}
      </span>
      {health.detail && (
        <span
          className={
            health.ok
              ? 'text-[10px] text-muted-foreground'
              : 'text-[10px] text-destructive/85'
          }
          title={health.detail}
        >
          {health.detail.length > 80 ? `${health.detail.slice(0, 80)}…` : health.detail}
        </span>
      )}
    </div>
  );
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.round((now - then) / 1000));
  if (diffSec < 5) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const min = Math.round(diffSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  return `${hr}h ago`;
}

function CapLine({ label, value }: { label: string; value: boolean }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1 text-muted-foreground">
      {value ? (
        <Check className="h-3 w-3 text-success" />
      ) : (
        <X className="h-3 w-3 text-muted-foreground/50" />
      )}
      {label}
    </span>
  );
}

function settlementHint(provider: PayoutProviderInfo | undefined): string {
  if (!provider) return '—';
  if (provider.capabilities.callbacks) return 'Marked paid when the provider reports back';
  if (provider.capabilities.syncStatus) return 'Checked hourly until paid';
  return 'Marked paid as soon as it is sent';
}
